import type { PluginConfig } from "../src/generalUtils";

import { ONETRUST_CONSENT_COOKIE_NAME } from "../src/generalUtils/consts";

import { getRegion, isAPAC, isStaging } from "../src/generalUtils/envFns";

import persadoPlugin from "../src/plugins/persado";
import datadogPlugin from "../src/plugins/datadog";
import quantumMetricPlugin from "../src/plugins/quantum-metric";
import adsPlugin from "../src/plugins/google-ads";
import { getConsentMap } from "../src/generalUtils/cookies";
import AutomatedLAMTransformations from "../src/plugins/transformation";
import GooglePlugin from "../src/plugins/google";
import { getDebug, logger } from "../src/debug";
import { ONETRUST_IDS } from "../src/plugins/onetrust";
import { getGaIdForRegion } from "../src/constants/gaIds";
import hightouchPlugin from "../src/plugins/hightouch";

logger.isDatadogEnabled = true;

export function getGaID() {
  if (typeof window === "undefined") {
    return;
  }
  const region = getRegion();
  return getGaIdForRegion(region, isStaging());
}

export function getOneTrustID() {
  const region = getRegion();
  console.log("Region detected for OneTrust ID:", region);
  const oneTrustID = ONETRUST_IDS[region];
  if (!oneTrustID) {
    return "";
  }
  if (typeof window === "undefined") return oneTrustID + "-test";
  return isStaging() ? oneTrustID + "-test" : oneTrustID;
}

export function getQuantumMetricScriptUrl() {
  // For NA regions (US and CA), use the standard script
  const region = getRegion();
  // TODO: This needs to be better formalized. Which regions should be NAM vs INTL.
  if (region === "us" || region === "ca" || region === "mx") {
    return "https://cdn.quantummetric.com/qscripts/quantum-lululemon.js";
  }
  // For all other regions (INTL), use the international script
  return "https://cdn.quantummetric.com/qscripts/quantum-lululemoninternational.js";
}

function gtagOptions() {
  const luluRegion = getRegion(); //commenting out until GTG is enabled for INTL regions
  let configCall: Record<PropertyKey, string | number | boolean> = {
    send_page_view: false,
  };

  if (luluRegion !== "us" && luluRegion !== "ca") {
    const correctedRegion = luluRegion === "uk" ? "gb" : luluRegion; // GTG uses 'gb' for the United Kingdom instead of 'uk'
    // Incase Alpine goes to Japan during the test window
    const transportPath = `/dcp/api/metrics/${correctedRegion !== "" ? correctedRegion + "/" : ""}`;

    configCall = {
      ...configCall,
      transport_url: `https://shop.lululemon.com${transportPath}`, //commenting out until GTG is enabled for INTL regions
      // first_party_collection: true //commenting out until GTG is enabled for INTL regions
    };
  }

  let isDebugging = getDebug();
  if (isDebugging) {
    configCall["debug_mode"] = isDebugging;
  }

  let configArray: unknown[][] = [
    ["js", new Date()],
    ["config", getGaID(), configCall],
  ];

  return configArray;
}

const dataLayerName = "automatedDataLayer";
function gtag2(..._args) {
  if (window[dataLayerName] === undefined) {
    window[dataLayerName] = window?.[dataLayerName] ?? [];
  }
  return window[dataLayerName].push(arguments);
}

const plugins: PluginConfig = {
  persado: {
    pluginFunction: persadoPlugin,
    consentLevel: ({ C0004 }) => Number(C0004) === 1,
    enabled: true,
    get debug() {
      return getDebug();
    },
    isAllowed() {
      if (typeof window === "undefined") {
        throw new Error("Not in browser context");
      }

      // Consent must be confirmed before Persado is allowed to load.
      // returns true when re-evaluated after consent-change grants C0004.
      if (Number(getConsentMap().C0004) !== 1) {
        return false;
      }

      // Persado is NA-only — only fire for US and CA regions.
      const region = getRegion();
      if (region !== "us" && region !== "ca") {
        return false;
      }
      // French-Canadian locale is excluded within CA.
      if (region === "ca" && window.location.pathname.includes("/fr-ca/")) {
        return false;
      }

      return true;
    },
  },
  automatedTransformations: {
    pluginFunction: AutomatedLAMTransformations,
    consentLevel: () => true, // In this case consent isn't required for this piece to run
    ignoreConsentUpdates: true,
    enabled: true,
    get debug() {
      return getDebug();
    },
    simulate: false, // This plugin is only used for testing and should not be used in production
    passHistory: true, // This plugin is only used for testing and should not be used in production
  },
  google: {
    pluginFunction: GooglePlugin,
    useGTG: true,
    enabled: true,
    get debug() {
      return getDebug();
    },
    gtagId: getGaID() as string,
    gtagDetails: gtagOptions,
    simulate: false, // This plugin is only used for testing and should not be used in production
    passHistory: true, // This plugin is only used for testing and should not be used in production
    dataLayerName,
    consentLevel: () => true,
    blockWithGPC: true, // This plugin should be aborted if GPC is detected
  },
  datadog: {
    pluginFunction: datadogPlugin,
    enabled: true,
    consentLevel: () => true,
    ignoreConsentUpdates: true,
    globalContext: {
      variant: "NA",
    },
    get debug() {
      return getDebug();
    },
  },
  quantumMetric: {
    pluginFunction: quantumMetricPlugin,
    enabled: true,
    get debug() {
      return getDebug();
    },
    scriptUrl: getQuantumMetricScriptUrl(),
    subName: "lululemon",
    loadNetworkInterceptor: false, // To be enabled in separate ticket
    consentLevel: ({ C0002 }) => {
      // All APAC regions (au, nz, jp, hk, kr) have no OneTrust ID and are not consent-gated
      if (isAPAC()) {
        return true;
      }
      // US, CA, and EMEA Alpine gate on C0002 (performance) — matches Adobe's classification
      return Number(C0002) === 1;
    },
    blockWithGPC: true, // This plugin should be aborted if GPC is detected
  },
  "google-ads": {
    dataLayerName,
    pluginFunction: adsPlugin,
    enabled: false,
    consentLevel: (ads) => {
      // This accounts for the logic w/i cookie interceptor that will "enable" anything with consent, but we want to respect the feature flag for Google Ads GTG.
      if (window?.["__analytics_ff"]?.isEnabled("google-ads-gtg") === false) {
        return false;
      }

      const { C0004 } = ads ?? {};

      if (isAPAC()) {
        return true;
      }

      return Number(C0004) === 1;
    },
    get debug() {
      return getDebug();
    },
    simulate: false,
  },
  hightouch: {
    pluginFunction: hightouchPlugin,
    enabled: true,
    consentLevel: () => true,
    get debug() {
      return getDebug();
    },
    isAllowed() {
      return isStaging();
    },
    simulate: false,
  },
};

interface GQLNetwork {
  operationName?: string;
  query: string;
  variables?: Record<string, string | number>;
}

export default {
  automatedEnabled: true,
  containerId: "GTM-PKLTKG52",
  dataLayerName,
  consentCookie: ONETRUST_CONSENT_COOKIE_NAME,
  getNextData() {},
  plugins,
  oneTrustId: getOneTrustID(),
  dedupeServices: {
    "page-view": [
      "amazon",
      "facebook",
      "magellan",
      "microsoft",
      "pinterest",
      "reddit",
      "snapchat",
      "tiktok",
      "metarouter",
    ],
    "add-to-cart": [
      "amazon",
      "facebook",
      "magellan",
      "microsoft",
      "pinterest",
      "reddit",
      "snapchat",
      "tiktok",
      "metarouter",
    ],
    "product-list-view": ["microsoft", "pinterest", "metarouter"],
    "product-view": [
      "amazon",
      "facebook",
      "magellan",
      "microsoft",
      "pinterest",
      "reddit",
      "snapchat",
      "tiktok",
      "metarouter",
    ],
    "checkout-start": [
      "facebook",
      "magellan",
      "snapchat",
      "tiktok",
      "metarouter",
    ],
    "cart-view": ["amazon", "microsoft", "metarouter"],
    purchase: [
      "amazon",
      "awin",
      "button",
      "facebook",
      "magellan",
      "microsoft",
      "pinterest",
      "reddit",
      "snapchat",
      "tiktok",
      "metarouter",
    ],
    "search-executed": ["microsoft", "pinterest", "metarouter"],
  },
  adobeUrl: isStaging()
    ? "//assets.adobedtm.com/7a84fdea953b/307bf509eec0/launch-ENe5f68e04f06c4acdb2bd8523619deaee-staging.min.js"
    : "//assets.adobedtm.com/7a84fdea953b/307bf509eec0/launch-ENbde5763c75c445f1853ee4e59787fde6.min.js",
  featureFlags: {
    "satellite-freeze": 100,
    "google-ads-gtg": 0,
  },
  isMwaPackaged() {
    // Adept Mind aka /a/ pages DO NOT package `mwa-analytics` since they're
    // SSR'd from a vendor. Therefore we return false for those pages
    return (
      (window.location.host === "stage.lululemon.com" ||
        window.location.host === "shop.lululemon.com") &&
      window.location.pathname.includes("/a/")
    );
  },
};
